import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Button, CircularProgress, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { useAppDispatch, useAppSelector } from '../store'
import { apiSlice } from '../store/api/apiSlice'
import { baseApi } from '../store/api/baseApi'
import { HIDE_ON_MOBILE_INLINE } from '../theme/responsive'

interface ReloadButtonProps {
  /** Tags trên `apiSlice` (reducerPath: 'api'). Khi omit → reset toàn bộ apiSlice cache; khi `[]` → bỏ qua apiSlice. */
  apiTags?: string[]
  /** Tags trên `baseApi` (reducerPath: 'tlcerpApi'). Khi omit → reset toàn bộ baseApi cache; khi `[]` → bỏ qua baseApi. */
  baseTags?: string[]
  /**
   * Loading state do caller cấp trực tiếp (vd: `summaryQuery.isFetching`).
   * Khi prop này là `true` → nút hiển thị spinner & disabled; `false`/omit → nút sẵn sàng.
   * Caller biết chính xác query nào cần chờ, nên cách này tránh việc quét slice
   * Redux (vốn dễ bị kẹt bởi pending query không liên quan của tab khác).
   */
  busy?: boolean
  /**
   * Tên endpoint (RTK Query) cần theo dõi loading state. Khi có bất kỳ query nào
   * có `endpointName` trong danh sách này đang `pending` trong apiSlice hoặc baseApi
   * → nút coi là busy. Dùng khi caller không tiện compute args để gọi selector trực
   * tiếp; vẫn là "Theo tab" vì caller quyết định endpoint nào theo tab đang active.
   */
  watchEndpoints?: string[]
  size?: 'small' | 'medium'
  sx?: SxProps<Theme>
  tooltip?: string
  /** String hoặc React node (vd `<Box sx={HIDE_ON_MOBILE_INLINE}>Reload</Box>`
   *  để icon-only mobile). Default `'Reload'`. */
  label?: ReactNode
  /**
   * Hook chạy ngay sau khi dispatch invalidate. Dùng khi caller cần
   * force refetch một query cụ thể (vd: summary report) — tag invalidation
   * sẽ phụ thuộc vào subscription, callback này luôn chạy.
   */
  onClick?: () => void
}

type RtkQueryEntry = { status?: string; endpointName?: string } | undefined
type RtkSlice = { queries?: Record<string, RtkQueryEntry> }

function sliceHasPendingEndpoint(slice: RtkSlice | undefined, endpoints: Set<string>): boolean {
  if (!slice?.queries || endpoints.size === 0) return false
  for (const entry of Object.values(slice.queries)) {
    if (entry?.status !== 'pending') continue
    if (entry.endpointName && endpoints.has(entry.endpointName)) return true
  }
  return false
}

const MIN_BUSY_MS = 350

export default function ReloadButton({
  apiTags,
  baseTags,
  busy: externalBusy,
  watchEndpoints,
  size = 'small',
  sx,
  tooltip = 'Reload data for the current tab.',
  label = 'Reload',
  onClick,
}: ReloadButtonProps) {
  const dispatch = useAppDispatch()
  const [justClicked, setJustClicked] = useState(false)
  const clickTimerRef = useRef<number | null>(null)

  const endpointsBusy = useAppSelector((s) => {
    if (!watchEndpoints?.length) return false
    const set = new Set(watchEndpoints)
    const state = s as Record<string, unknown>
    return (
      sliceHasPendingEndpoint(state[apiSlice.reducerPath] as RtkSlice | undefined, set) ||
      sliceHasPendingEndpoint(state[baseApi.reducerPath] as RtkSlice | undefined, set)
    )
  })
  const busy = justClicked || Boolean(externalBusy) || endpointsBusy

  useEffect(() => {
    return () => {
      if (clickTimerRef.current !== null) window.clearTimeout(clickTimerRef.current)
    }
  }, [])

  const handleReload = useCallback(() => {
    setJustClicked(true)
    if (clickTimerRef.current !== null) window.clearTimeout(clickTimerRef.current)
    clickTimerRef.current = window.setTimeout(() => {
      setJustClicked(false)
      clickTimerRef.current = null
    }, MIN_BUSY_MS)

    if (apiTags === undefined) {
      dispatch(apiSlice.util.resetApiState())
    } else if (apiTags.length > 0) {
      dispatch(apiSlice.util.invalidateTags(apiTags as never[]))
    }
    if (baseTags === undefined) {
      dispatch(baseApi.util.resetApiState())
    } else if (baseTags.length > 0) {
      dispatch(baseApi.util.invalidateTags(baseTags as never[]))
    }
    onClick?.()
  }, [dispatch, apiTags, baseTags, onClick])

  return (
    <Tooltip title={tooltip}>
      <span>
        <Button
          size={size}
          variant="outlined"
          onClick={handleReload}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={11} color="inherit" /> : <RefreshIcon fontSize="small" />}
          sx={sx}
          title={typeof label === 'string' ? label : undefined}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{label}</Box>
        </Button>
      </span>
    </Tooltip>
  )
}
